import type { UseFormReturn, FieldErrors, FieldValues, Path, PathValue, FieldError as RHFFieldError } from "react-hook-form"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { FieldError } from "@/components/shared/field-error"
import {
  destinationTypeValues,
  destinationTypeLabels,
  type DestinationTypeValue,
} from "@/lib/schemas"

type TypeConfigForm<T extends FieldValues> = UseFormReturn<T>

interface DestinationTypeSelectorProps<T extends FieldValues> {
  form: TypeConfigForm<T>
  destType: DestinationTypeValue
}

export function DestinationTypeSelector<T extends FieldValues>({ form, destType }: DestinationTypeSelectorProps<T>) {
  return (
    <div className="space-y-2">
      <Label htmlFor="type">Destination Type</Label>
      <Select
        value={destType}
        onValueChange={(v) => form.setValue("type" as Path<T>, v as PathValue<T, Path<T>>, { shouldValidate: true })}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {destinationTypeValues.map((t) => (
            <SelectItem key={t} value={t}>{destinationTypeLabels[t]}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

interface DestinationTypeConfigFieldsProps<T extends FieldValues> {
  form: TypeConfigForm<T>
  destType: DestinationTypeValue
  errors: FieldErrors<T>
}

export function DestinationTypeConfigFields<T extends FieldValues>({ form, destType, errors }: DestinationTypeConfigFieldsProps<T>) {
  if (destType === "HTTP") return null
  if (destType === "CONNECTOR") return null

  const reg = (name: string) => form.register(name as Path<T>)
  const err = (name: string) => (errors as Record<string, RHFFieldError | undefined>)[name]
  const watch = (name: string) => form.watch(name as Path<T>)
  const setVal = (name: string, val: string) => form.setValue(name as Path<T>, val as PathValue<T, Path<T>>)

  return (
    <div className="space-y-3 rounded-lg glass-card p-4">
      <Label className="text-xs font-medium">{destinationTypeLabels[destType]} Configuration</Label>

      {destType === "SQS" && (
        <div className="space-y-3 pt-1">
          <div className="space-y-1">
            <Label htmlFor="sqsQueueUrl" className="text-xs text-muted-foreground">Queue URL</Label>
            <Input id="sqsQueueUrl" className="font-mono text-sm" placeholder="https://sqs.us-east-1.amazonaws.com/123456789/my-queue" {...reg("sqsQueueUrl")} />
            <FieldError error={err("sqsQueueUrl")} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="sqsRegion" className="text-xs text-muted-foreground">Region</Label>
            <Input id="sqsRegion" placeholder="us-east-1" {...reg("sqsRegion")} />
            <FieldError error={err("sqsRegion")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="sqsAccessKeyId" className="text-xs text-muted-foreground">Access Key ID</Label>
              <Input id="sqsAccessKeyId" {...reg("sqsAccessKeyId")} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="sqsSecretAccessKey" className="text-xs text-muted-foreground">Secret Access Key</Label>
              <Input id="sqsSecretAccessKey" type="password" {...reg("sqsSecretAccessKey")} />
            </div>
          </div>
        </div>
      )}

      {destType === "EVENTBRIDGE" && (
        <div className="space-y-3 pt-1">
          <div className="space-y-1">
            <Label htmlFor="ebEventBusName" className="text-xs text-muted-foreground">Event Bus Name</Label>
            <Input id="ebEventBusName" placeholder="default" {...reg("ebEventBusName")} />
            <FieldError error={err("ebEventBusName")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="ebSource" className="text-xs text-muted-foreground">Source</Label>
              <Input id="ebSource" placeholder="com.myapp.webhooks" {...reg("ebSource")} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ebDetailType" className="text-xs text-muted-foreground">Detail Type</Label>
              <Input id="ebDetailType" placeholder="webhook.event" {...reg("ebDetailType")} />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="ebRegion" className="text-xs text-muted-foreground">Region</Label>
            <Input id="ebRegion" placeholder="us-east-1" {...reg("ebRegion")} />
            <FieldError error={err("ebRegion")} />
          </div>
        </div>
      )}

      {destType === "PUBSUB" && (
        <div className="space-y-3 pt-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="psProjectId" className="text-xs text-muted-foreground">Project ID</Label>
              <Input id="psProjectId" placeholder="my-gcp-project" {...reg("psProjectId")} />
              <FieldError error={err("psProjectId")} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="psTopicId" className="text-xs text-muted-foreground">Topic ID</Label>
              <Input id="psTopicId" placeholder="my-topic" {...reg("psTopicId")} />
              <FieldError error={err("psTopicId")} />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="psCredentialsJson" className="text-xs text-muted-foreground">Credentials JSON</Label>
            <Textarea
              id="psCredentialsJson"
              className="font-mono text-xs min-h-20"
              placeholder='{"type": "service_account", ...}'
              {...reg("psCredentialsJson")}
            />
          </div>
        </div>
      )}

      {destType === "KAFKA" && (
        <div className="space-y-3 pt-1">
          <div className="space-y-1">
            <Label htmlFor="kafkaBrokers" className="text-xs text-muted-foreground">Brokers (comma-separated)</Label>
            <Input id="kafkaBrokers" className="font-mono text-sm" placeholder="broker1:9092, broker2:9092" {...reg("kafkaBrokers")} />
            <FieldError error={err("kafkaBrokers")} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="kafkaTopic" className="text-xs text-muted-foreground">Topic</Label>
            <Input id="kafkaTopic" placeholder="webhooks" {...reg("kafkaTopic")} />
            <FieldError error={err("kafkaTopic")} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="kafkaSaslMechanism" className="text-xs text-muted-foreground">SASL Mechanism</Label>
            <Select
              value={(watch("kafkaSaslMechanism") as string) ?? ""}
              onValueChange={(v) => setVal("kafkaSaslMechanism", v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">None</SelectItem>
                <SelectItem value="PLAIN">PLAIN</SelectItem>
                <SelectItem value="SCRAM-SHA-256">SCRAM-SHA-256</SelectItem>
                <SelectItem value="SCRAM-SHA-512">SCRAM-SHA-512</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {watch("kafkaSaslMechanism") && (watch("kafkaSaslMechanism") as string) !== "" && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="kafkaSaslUsername" className="text-xs text-muted-foreground">SASL Username</Label>
                <Input id="kafkaSaslUsername" {...reg("kafkaSaslUsername")} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="kafkaSaslPassword" className="text-xs text-muted-foreground">SASL Password</Label>
                <Input id="kafkaSaslPassword" type="password" {...reg("kafkaSaslPassword")} />
              </div>
            </div>
          )}
        </div>
      )}

      {destType === "RABBITMQ" && (
        <div className="space-y-3 pt-1">
          <div className="space-y-1">
            <Label htmlFor="rmqUrl" className="text-xs text-muted-foreground">URL</Label>
            <Input id="rmqUrl" className="font-mono text-sm" placeholder="amqp://user:pass@host:5672/vhost" {...reg("rmqUrl")} />
            <FieldError error={err("rmqUrl")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="rmqExchange" className="text-xs text-muted-foreground">Exchange</Label>
              <Input id="rmqExchange" placeholder="webhooks" {...reg("rmqExchange")} />
              <FieldError error={err("rmqExchange")} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="rmqRoutingKey" className="text-xs text-muted-foreground">Routing Key</Label>
              <Input id="rmqRoutingKey" placeholder="events" {...reg("rmqRoutingKey")} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
